import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchCouponUsersAction } from "../../redux/actions/couponActions";
import {
  MdConfirmationNumber,
  MdPerson,
  MdEmail,
  MdCalendarToday,
  MdDiscount,
  MdAccessTime,
  MdTrendingUp,
} from "react-icons/md";
import { FaSearch, FaTicketAlt, FaUsers, FaChartLine } from "react-icons/fa";
import Pagination from "../../../helper/Pagination";
import "./CouponDetailsModal.css";

const CouponDetailsOffcanvas = ({ isOpen, onClose, coupon }) => {
  const dispatch = useDispatch();
  const { couponUsers, usersPagination, usersLoading, usersError } =
    useSelector((state) => state.coupon);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (isOpen && coupon?._id) {
      setCurrentPage(1);
      setSearchTerm("");
    }
  }, [isOpen, coupon?._id]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen && coupon?._id) {
      dispatch(
        fetchCouponUsersAction({
          search: searchTerm,
          page: currentPage,
          limit: 10,
          couponId: coupon._id,
        }),
      );
    }
  }, [dispatch, isOpen, coupon?._id, searchTerm, currentPage]);

  if (!coupon) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size="xl"
      centered
      className="coupon-modal"
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="d-flex align-items-center justify-content-center">
          <div className="modal-icon">
            <FaTicketAlt />
          </div>
          <div className="align-items-center">
            <h5 className="mb-0">{coupon.name}</h5>
            <p className="fs-6 mt-0 mb-0">{coupon.stripeCouponId}</p>
          </div>
        </Modal.Title>
      </Modal.Header>

      {/*  scroll-container */}
      <Modal.Body className="p-0 modal-body-custom">
        {/* Stats Overview */}
        <div className="modal-stats-section">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="stat-card discount-card">
                <div className="stat-icon">
                  <MdDiscount />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {coupon.percent_off
                      ? `${coupon.percent_off}%`
                      : `${coupon.currency === "jpy" ? "¥" : "$"}${
                          coupon.currency === "jpy"
                            ? coupon.amount_off
                            : (coupon.amount_off / 100).toFixed(2)
                        }`}
                  </div>
                  <div className="stat-label">Discount</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card usage-card">
                <div className="stat-icon">
                  <FaChartLine />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{coupon.usedCount}</div>
                  <div className="stat-label">Total Usage</div>
                </div>
              </div>
            </div>
            {/* <div className="col-md-3">
              <div className="stat-card users-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{coupon.uniqueUsers}</div>
                  <div className="stat-label">Unique Users</div>
                </div>
              </div>
            </div> */}
            <div className="col-md-4">
              <div className="stat-card status-card">
                <div className="stat-icon">
                  <MdAccessTime />
                </div>
                <div className="stat-content">
                  <div
                    className={`stat-value status-${
                      coupon.valid ? "active" : "inactive"
                    }`}
                  >
                    {coupon.valid ? "Active" : "Inactive"}
                  </div>
                  <div className="stat-label">Status</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coupon Details */}
        <div className="coupon-details-section">
          <h6 className="section-title">
            <MdConfirmationNumber className="me-2" />
            Coupon Information
          </h6>
          <div className="details-grid">
            <div className="detail-item">
              <div className="detail-label">Currency</div>
              <div className="detail-value">
                <span className="badge bg-secondary">
                  {coupon.currency?.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Duration</div>
              <div className="detail-value text-capitalize">
                {coupon.duration}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Max Redemptions</div>
              <div className="detail-value">
                {coupon.max_redemptions || "Unlimited"}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-label">Times Redeemed</div>
              <div className="detail-value">{coupon.times_redeemed}</div>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="users-section">
          <div className="users-header">
            <h6 className="section-title mb-0">
              <FaUsers className="me-2" />
              Coupon Users
              <span className="badge bg-primary ms-2">
                {couponUsers?.length || 0}
              </span>
            </h6>
            <div className="position-relative d-flex justify-content-end mb-1">
              <input
                type="search"
                placeholder="Search coupons..."
                className="form-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "200px" }}
              />
              {searchTerm === "" && (
                <div
                  className="position-absolute"
                  style={{
                    top: "50%",
                    right: "13px",
                    transform: "translateY(-50%)",
                    color: "#666",
                  }}
                >
                  <FaSearch />
                </div>
              )}
            </div>
          </div>

          <div
            className="users-content "
            style={{
              overflowY: "auto",
              scrollbarWidth: "thin",
            }}
          >
            {usersLoading ? (
              <div className="loading-state">
                <div
                  className="spinner-border text-primary"
                  role="status"
                ></div>
                <p className="mt-3 mb-0">Loading users...</p>
              </div>
            ) : usersError ? (
              <div className="error-state">
                <div className="alert alert-danger">
                  <strong>Error:</strong> {usersError}
                </div>
              </div>
            ) : couponUsers && couponUsers.length > 0 ? (
              <div className="table-container">
                <table className="partners-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User Details</th>
                      <th>Domain</th>
                      <th>Plan</th>
                      <th>Used Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponUsers.map((usage, index) => (
                      <tr key={usage._id}>
                        <td>
                          <div className="user-number">
                            {(currentPage - 1) * usersPagination.itemsPerPage +
                              index +
                              1}
                          </div>
                        </td>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              <MdPerson />
                            </div>
                            <div>
                              <div className="user-name text-start">
                                {`${usage.user?.firstName || ""} ${
                                  usage.user?.lastName || ""
                                }`.trim() || "N/A"}
                              </div>
                              <div className="user-email">
                                <MdEmail size={14} className="me-1" />
                                {usage.user?.email || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="domain-badge">
                            {usage.user?.domain || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span className="plan-badge">
                            {usage.plan?.name || "N/A"}
                          </span>
                        </td>
                        <td>
                          <div className="date-info">
                            <MdCalendarToday size={14} className="me-1" />
                            {formatDate(usage.usedDate)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FaUsers className="empty-icon" />
                <p className="empty-text">No users have used this coupon yet</p>
              </div>
            )}

            {couponUsers && couponUsers.length > 0 && (
              <div className="mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={usersPagination.totalPages}
                  totalItems={usersPagination.totalItems}
                  itemsPerPage={usersPagination.itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CouponDetailsOffcanvas;
